import React, { useState } from 'react';
import { Box, IconButton, List, ListItem, ListItemIcon, ListItemText, Divider, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import FeedbackIcon from '@mui/icons-material/Feedback';
import DashboardIcon from '@mui/icons-material/Dashboard';
import menuItems from '../../utils/menuConfig';
import { useLanguage } from '../../contexts/LanguageContext';
import { onExplicitUiLangSwitch } from '../../services/langPrefs';
import ThaiFlag from '../Header/thai-flag.png';
import EnglishFlag from '../Header/english-flag.png';
import ChinaFlag from '../Header/china-flag.png';
import CategorySelectionModal from '../InputPages/CategorySelectionModal';

const MobileMenuPanel = ({ onClose, onNavigate, isAuthenticated, onSearch, logout }) => {
  const { language, changeLanguage } = useLanguage();
  const menuConfig = menuItems(isAuthenticated, language);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);

  const handleLanguageChange = (newLanguage) => {
    console.log('[MobileMenuPanel] Language change requested:', newLanguage);
    changeLanguage(newLanguage);
    // Map CN to ZH for langPrefs (langPrefs uses ZH internally)
    const langForPrefs = newLanguage === 'CN' ? 'ZH' : newLanguage;
    onExplicitUiLangSwitch(langForPrefs);

    // Close menu after language change for better UX
    setTimeout(() => {
      onClose();
    }, 100);

    // Note: The language change will automatically trigger a re-fetch via the
    // useEffect in Home.js that watches the language dependency
  };

  const getMenuItemIcon = (item) => {
    const { label = '', action } = item;
    if (action === 'dashboard') {
      return <DashboardIcon />;
    }
    if (label.includes('QR') || label.includes('扫描') || label.includes('สแกน')) {
      return <QrCodeScannerIcon />;
    }
    if (label.includes('Travy') || label.includes('Tvent') || label.includes('旅游团') || label.includes('ทัวร์กลุ่ม')) {
      return <PeopleAltIcon />;
    }
    if (label.includes('Event') || label.includes('活动') || label.includes('กิจกรรม') || label.includes('Adding') || label.includes('添加') || label.includes('เพิ่ม')) {
      return <PlaylistAddIcon />;
    }
    return null;
  };

  const getTitle = () => {
    switch (language) {
      case 'TH':
        return 'เมนู';
      case 'CN':
        return '菜单';
      default:
        return 'Menu';
    }
  };

  const handleMenuItemClick = async (item) => {
    if (item.action === 'logout') {
      await logout();
      onClose();
      onNavigate('/');
    } else if (item.action === 'add-data') {
      onClose();
      setCategoryModalOpen(true);
    } else {
      onNavigate(item.path);
    }
  };

  return (
    <>
      <Box sx={{
        width: '100%',
        maxHeight: '60vh',
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        backgroundColor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 1,
        paddingTop: 'env(safe-area-inset-top)'
      }}>
      {/* Header Row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1 }}>
        <IconButton aria-label="Close menu" onClick={onClose}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ fontWeight: 600, fontSize: 16 }}>
          {getTitle()}
        </Box>
        <IconButton aria-label="Close menu" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      {/* Menu Items */}
      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        <List dense>
          {menuConfig.map((item, index) => {
            const icon = getMenuItemIcon(item);
            return (
              <ListItem key={index} button onClick={() => handleMenuItemClick(item)}>
                {icon && <ListItemIcon>{icon}</ListItemIcon>}
                <ListItemText primary={item.label} />
              </ListItem>
            );
          })}
          <ListItem button onClick={() => { onClose(); window.open('https://flannel-chalk-c55.notion.site/347645f7a9f680978eafc56c0bdda911?pvs=105', '_blank', 'noopener'); }}>
            <ListItemIcon><FeedbackIcon /></ListItemIcon>
            <ListItemText primary="ส่งข้อเสนอแนะ" />
          </ListItem>
        </List>
      </Box>

      <Divider />

      {/* Language Selection */}
      <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', fontSize: '0.7rem', flexShrink: 0 }}>
          {language === 'TH' ? 'ภาษา' : language === 'CN' ? '语言' : 'Language'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flex: 1, justifyContent: 'center' }}>
          <IconButton
            onClick={() => handleLanguageChange('TH')}
            aria-label="Thai language"
            sx={{
              padding: '4px',
              border: language === 'TH' ? '2px solid #1976d2' : '2px solid #e0e0e0',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: '#f0f0f0'
              }
            }}
          >
            <img src={ThaiFlag} alt="TH" width={24} height={18} style={{ display: 'block' }} />
          </IconButton>
          <IconButton
            onClick={() => handleLanguageChange('EN')}
            aria-label="English language"
            sx={{
              padding: '4px',
              border: language === 'EN' ? '2px solid #1976d2' : '2px solid #e0e0e0',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: '#f0f0f0'
              }
            }}
          >
            <img src={EnglishFlag} alt="EN" width={24} height={18} style={{ display: 'block' }} />
          </IconButton>
          <IconButton
            onClick={() => handleLanguageChange('CN')}
            aria-label="Chinese language"
            sx={{
              padding: '4px',
              border: language === 'CN' ? '2px solid #1976d2' : '2px solid #e0e0e0',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: '#f0f0f0'
              }
            }}
          >
            <img src={ChinaFlag} alt="CN" width={24} height={18} style={{ display: 'block' }} />
          </IconButton>
        </Box>
      </Box>
      </Box>

      {/* Category Selection Modal */}
      <CategorySelectionModal
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        language={language}
      />
    </>
  );
};

export default MobileMenuPanel;
